import { useEffect, useMemo, useState } from "react";
import { useGetMany, useRecordContext } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import { shouldShowScannedAttendance } from "./should-show-scanned-attendance";
import type { AttendanceTurnoutData } from "./MiqaatAttendanceAuditCard";

type RsvpDemographics = {
  totalRsvpAttendees: number;
  male: number;
  female: number;
  childrenUnder10: number;
  scanned?: AttendanceTurnoutData;
};

type Props = {
  /** When true, render only the inner stats (for use inside a parent Card). */
  embedded?: boolean;
  /** When true, always show scanned attendance once loaded (including zeros). */
  alwaysShowAttendance?: boolean;
};

function StatBlock({ value, label }: { value: number; label: string }) {
  return (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Typography
        variant="h5"
        fontWeight={600}
        color="text.primary"
        component="p"
        sx={{ lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Grid>
  );
}

export default function MiqaatRsvpDemographics({
  embedded = false,
  alwaysShowAttendance = false,
}: Props) {
  const record = useRecordContext();
  const venueIds = useMemo(() => {
    const ids = record?.venueIds;
    return Array.isArray(ids) ? ids.filter((id): id is string => Boolean(id)) : [];
  }, [record?.venueIds]);
  const { data: venues } = useGetMany(
    "miqaatVenues",
    { ids: venueIds },
    { enabled: venueIds.length > 0 }
  );
  const [venueId, setVenueId] = useState("");
  const [stats, setStats] = useState<RsvpDemographics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (venueId && venueIds.length > 0 && !venueIds.includes(venueId)) {
      setVenueId("");
    }
  }, [venueId, venueIds]);

  useEffect(() => {
    const id = record?.id;
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void callApi({
      location: "miqaats",
      method: "GET",
      id: `${String(id)}/rsvp-demographics`,
      data: venueId ? { venueId } : {},
    })
      .then((res) => {
        if (!cancelled) {
          setStats((res.data ?? null) as RsvpDemographics | null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load RSVP stats");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [record?.id, venueId]);

  if (!record?.id) {
    return null;
  }

  const venueChoices = venueIds.map((id) => {
    const venue = (venues ?? []).find((v) => String(v.id) === id);
    return { id, name: venue?.name ? String(venue.name) : id };
  });

  const body = (
    <>
      {!embedded && (
        <Typography
          variant="overline"
          color="text.secondary"
          display="block"
          sx={{ letterSpacing: 0.5 }}
        >
          WhatsApp RSVP · this occurrence
        </Typography>
      )}
      {!embedded && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1, mb: 1.5, maxWidth: 560 }}
        >
          Male, female, and children mirror ITS data. Total counts every person on the RSVP.
        </Typography>
      )}
      {venueChoices.length > 0 && (
        <Box sx={{ mb: 2, maxWidth: 360 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="miqaat-rsvp-venue-filter-label">Filter by venue</InputLabel>
            <Select
              labelId="miqaat-rsvp-venue-filter-label"
              label="Filter by venue"
              value={venueId}
              onChange={(e) => setVenueId(String(e.target.value))}
            >
              <MenuItem value="">
                <em>All venues</em>
              </MenuItem>
              {venueChoices.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {loading && <CircularProgress size={22} sx={{ my: 1 }} />}
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {!loading && !error && stats && (
        <>
          {(!embedded || alwaysShowAttendance) && (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              RSVP
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <StatBlock value={stats.male} label="Male" />
            <StatBlock value={stats.female} label="Female" />
            <StatBlock value={stats.childrenUnder10} label="Children (<10)" />
            <StatBlock value={stats.totalRsvpAttendees} label="Total persons" />
          </Grid>
          {shouldShowScannedAttendance(
            stats.scanned?.totalScannedAttendees,
            alwaysShowAttendance
          ) && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
                Scanned attendance
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <StatBlock value={stats.scanned?.male ?? 0} label="Male" />
                <StatBlock value={stats.scanned?.female ?? 0} label="Female" />
                <StatBlock value={stats.scanned?.childrenUnder10 ?? 0} label="Children (<10)" />
                <StatBlock
                  value={stats.scanned?.totalScannedAttendees ?? 0}
                  label="Total persons"
                />
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return <Box>{body}</Box>;
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>{body}</CardContent>
    </Card>
  );
}
